import React from 'react';
import { Seo } from '@/components/common/Seo';
import { FadeIn } from '@/components/common/FadeIn';
import { SectionLabel } from '@/components/common/SectionLabel';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { MarketsMap } from '@/components/graphics/MarketsMap';
import { useContent } from '@/context/ContentContext';

export default function About() {
  const { content } = useContent();
  const seo = content?.seo?.about;
  const principles = [
    'Professional communication',
    'Responsible opportunity evaluation',
    'Authentic commercial supply relationships',
    'Respect for brand standards',
    'Clear documentation requirements',
    'Long-term partnership thinking',
  ];
  return (
    <>
      <Seo title={seo?.title} description={seo?.description} />
      <PageHero
        label="About Ophir Supply"
        title="Building dependable commercial relationships"
        intro="OPHIR SUPPLY is a North American wholesale, distribution and global-sourcing company. We develop professional relationships with manufacturers, brand owners, importers, authorized distributors, suppliers, retailers and qualified business buyers."
        image="/images/ophir/about/planning.webp"
        imageAlt="Business professionals reviewing product samples, a printed catalogue and supply documents in a bright meeting room"
      />

      <section className="bg-white">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            <FadeIn className="max-w-[62ch]">
              <SectionLabel>Who we are</SectionLabel>
              <h2 className="mt-5 font-serif text-[28px] sm:text-[34px] leading-tight text-ophir-navy-deep">A careful, commercial approach to supply</h2>
              <p className="mt-5 text-[16.5px] leading-relaxed text-ophir-muted">
                We source quality products internationally while primarily serving suitable Canadian and U.S. commercial opportunities. Our work is grounded in professional communication, responsible product evaluation and respect for the brands and channels we represent.
              </p>
              <p className="mt-4 text-[16.5px] leading-relaxed text-ophir-muted">
                We focus on organised, well-documented supply relationships that stand the test of time — connecting quality goods with appropriate demand across North America.
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-8 shadow-ophir">
                <h3 className="font-serif text-[20px] text-ophir-navy-deep">Our trust principles</h3>
                <ul className="mt-5 space-y-3.5">
                  {principles.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-[15.5px] text-ophir-navy">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-ophir-gold shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeIn>
              <SectionLabel>Markets</SectionLabel>
              <h2 className="mt-5 font-serif text-[28px] sm:text-[34px] leading-tight text-ophir-navy-deep">Global Sourcing. North American Focus.</h2>
              <p className="mt-5 text-[16.5px] leading-relaxed text-ophir-muted">
                We maintain supplier relationships across multiple markets and channel that reach toward dependable service of Canadian and U.S. commercial opportunities.
              </p>
              <div className="mt-8"><OphirButton to="/capabilities">Explore Our Capabilities</OphirButton></div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="rounded-2xl border border-ophir-grey bg-white p-6 shadow-ophir">
                <MarketsMap className="w-full h-auto" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </>
  );
}
